import { RefreshCw } from 'lucide-react'

/**
 * Esqueletos del landing público mientras se carga el contenido real del CMS
 * (sin contenido inventado: la silueta imita las secciones reales para que el
 * reemplazo no salte). Se muestran solo en la primera visita; las siguientes
 * usan la caché del último contenido real.
 */

function Block({ className }: { className: string }) {
  return <div className={`animate-pulse rounded bg-neutral-100 ${className}`} />
}

function HeroSkeleton() {
  return (
    <section className="pt-12 lg:pt-20 pb-20 lg:pb-32">
      <div className="max-w-7xl mx-auto px-5 lg:px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div className="space-y-7 lg:space-y-8">
          <Block className="h-6 w-48 rounded-full" />
          <div className="space-y-3">
            <Block className="h-12 lg:h-20 w-full max-w-lg" />
            <Block className="h-12 lg:h-20 w-3/4" />
          </div>
          <div className="space-y-2 max-w-xl">
            <Block className="h-5 w-full" />
            <Block className="h-5 w-5/6" />
          </div>
          <div className="flex flex-wrap gap-3">
            <Block className="h-[52px] w-44 rounded-xl" />
            <Block className="h-[52px] w-36 rounded-xl" />
          </div>
          <div className="flex items-center gap-5 pt-2">
            <div className="flex -space-x-2">
              <div className="size-7 animate-pulse rounded-full border-2 border-white bg-neutral-100" />
              <div className="size-7 animate-pulse rounded-full border-2 border-white bg-neutral-100" />
              <div className="size-7 animate-pulse rounded-full border-2 border-white bg-neutral-100" />
            </div>
            <Block className="h-4 w-40" />
          </div>
        </div>
        <Block className="hidden lg:block w-full aspect-[5/4] rounded-3xl" />
      </div>
    </section>
  )
}

function LogoStripSkeleton() {
  return (
    <section className="py-10">
      <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-center gap-x-10 gap-y-4 px-5 lg:px-6">
        {Array.from({ length: 6 }, (_, i) => (
          <Block key={i} className="h-6 w-28" />
        ))}
      </div>
    </section>
  )
}

function CardsSkeleton({ count, cardClass }: { count: number; cardClass: string }) {
  return (
    <section className="py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-6">
        <div className="mx-auto mb-12 max-w-xl space-y-3 text-center">
          <Block className="mx-auto h-8 w-72" />
          <Block className="mx-auto h-4 w-full max-w-md" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: count }, (_, i) => (
            <Block key={i} className={cardClass} />
          ))}
        </div>
      </div>
    </section>
  )
}

export function LandingSkeleton() {
  return (
    <div role="status" aria-busy="true" aria-live="polite">
      <span className="sr-only">Cargando el contenido…</span>
      <HeroSkeleton />
      <LogoStripSkeleton />
      <CardsSkeleton count={4} cardClass="h-72 rounded-2xl" />
      <CardsSkeleton count={4} cardClass="h-96 rounded-2xl" />
    </div>
  )
}

/**
 * Estado de error del landing: agotados los reintentos y sin caché de una
 * visita anterior. Sin contenido inventado — mensaje honesto + reintentar.
 */
export function LandingLoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <section className="grid min-h-[60vh] place-items-center px-5 py-24">
      <div className="max-w-md space-y-4 text-center">
        <h1 className="text-2xl font-bold">No pudimos cargar el contenido</h1>
        <p className="text-muted-foreground">
          Revisa tu conexión a internet e inténtalo de nuevo. El sitio se
          mostrará en cuanto podamos traer la información.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-2 rounded-xl bg-foreground px-7 py-4 font-semibold text-background transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          <RefreshCw className="size-4" /> Reintentar
        </button>
      </div>
    </section>
  )
}
